import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import {
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';

const UserProfileChangePasswordSection: React.FC = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ text: string; severity: 'success' | 'error' } | null>(null);
  const [expanded, setExpanded] = useState(false);

  const resetFields = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setShowCurrent(false);
    setShowNew(false);
    setShowConfirm(false);
  };

  const handleChangePassword = async () => {
    setMessage(null);
    if (!currentPassword.trim()) {
      setMessage({ text: 'Informe sua senha atual.', severity: 'error' });
      return;
    }
    if (newPassword.length < 6) {
      setMessage({ text: 'A nova senha deve ter pelo menos 6 caracteres.', severity: 'error' });
      return;
    }
    if (newPassword !== confirmPassword) {
      setMessage({ text: 'A confirmação não coincide com a nova senha.', severity: 'error' });
      return;
    }
    if (currentPassword === newPassword) {
      setMessage({ text: 'A nova senha deve ser diferente da senha atual.', severity: 'error' });
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${getApiUrl()}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword,
      });
      resetFields();
      setExpanded(false);
      setMessage({ text: 'Senha alterada com sucesso.', severity: 'success' });
    } catch (err: any) {
      setMessage({
        text: err.response?.data?.error || 'Não foi possível alterar a senha.',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const visibilityToggle = (show: boolean, setShow: (v: boolean) => void) => (
    <InputAdornment position="end">
      <IconButton
        aria-label={show ? 'Ocultar senha' : 'Mostrar senha'}
        onClick={() => setShow(!show)}
        edge="end"
        size="small"
      >
        {show ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
      </IconButton>
    </InputAdornment>
  );

  return (
    <>
      <Divider sx={{ my: 3 }} />
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, mb: 1.5 }}>
        <LockIcon sx={{ color: '#0d9488', mt: 0.25 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a' }}>
            Alterar senha
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.25 }}>
            Use uma senha forte e não compartilhe com outras pessoas.
          </Typography>
        </Box>
      </Box>

      {!expanded ? (
        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            setMessage(null);
            setExpanded(true);
          }}
          sx={{ textTransform: 'none', borderColor: '#cbd5e1', color: '#334155' }}
        >
          Definir nova senha
        </Button>
      ) : (
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            border: '1px solid rgba(13, 148, 136, 0.2)',
            bgcolor: 'rgba(13, 148, 136, 0.04)',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              size="small"
              label="Senha atual"
              type={showCurrent ? 'text' : 'password'}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
              InputProps={{ endAdornment: visibilityToggle(showCurrent, setShowCurrent) }}
            />
            <TextField
              fullWidth
              size="small"
              label="Nova senha"
              type={showNew ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              helperText="Mínimo de 6 caracteres"
              InputProps={{ endAdornment: visibilityToggle(showNew, setShowNew) }}
            />
            <TextField
              fullWidth
              size="small"
              label="Confirmar nova senha"
              type={showConfirm ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              InputProps={{ endAdornment: visibilityToggle(showConfirm, setShowConfirm) }}
            />
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
            <Button
              variant="contained"
              size="small"
              disabled={saving}
              onClick={() => void handleChangePassword()}
              sx={{ textTransform: 'none', bgcolor: '#0d9488', '&:hover': { bgcolor: '#0f766e' } }}
            >
              {saving ? <CircularProgress size={16} color="inherit" /> : 'Salvar nova senha'}
            </Button>
            <Button
              size="small"
              disabled={saving}
              onClick={() => {
                resetFields();
                setExpanded(false);
                setMessage(null);
              }}
              sx={{ textTransform: 'none' }}
            >
              Cancelar
            </Button>
          </Box>
        </Box>
      )}

      {message && (
        <Alert severity={message.severity} sx={{ mt: 2, borderRadius: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}
    </>
  );
};

export default UserProfileChangePasswordSection;
